import { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  Group,
  ActionIcon,
  Tooltip,
  Collapse,
  Badge,
  Menu,
  Skeleton,
} from '@mantine/core';
import {
  House,
  Buildings,
  AppWindow,
  Eye,
  Lock,
  Microphone,
  Database,
  ClipboardText,
  RocketLaunch,
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  Users,
  FolderSimple,
  Globe,
  GitBranch,
  PuzzlePiece,
  TreeStructure,
  CaretDown,
  CaretRight,
  Plus,
  SquaresFour,
  DeviceMobile,
} from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/auth.store';
import { useUIStore } from '../../store/ui.store';
import { useDeveloperApps } from '../../hooks/developer/useDeveloperApps';
import { useIsDeveloperSuperAdmin } from '../../hooks/useDeveloperPermissions';
import styles from './Sidebar.module.css';

interface NavItem {
  label: string;
  icon: React.ElementType;
  path: string;
  disabled?: boolean;
}

const ADMIN_NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Platform Overview',
    items: [
      { label: 'Dashboard', icon: House, path: '/dashboard' },
      { label: 'Providers', icon: Buildings, path: '/providers' },
      { label: 'Applications', icon: AppWindow, path: '/admin/apps' },
      { label: 'Admin Accounts', icon: ShieldCheck, path: '/admins' },
    ],
  },
  {
    label: 'App Submissions',
    items: [
      { label: 'All Review Queue', icon: Eye, path: '/review' },
      { label: 'Version Reviews', icon: GitBranch, path: '/review?type=VERSION_REVIEW' },
      { label: 'Web App Reviews', icon: AppWindow, path: '/review?type=APP_REVIEW' },
      { label: 'Domain Whitelist', icon: Globe, path: '/review?type=DOMAIN_CHANGE' },
      { label: 'Capability Requests', icon: PuzzlePiece, path: '/review?type=CAPABILITY_CREATE' },
      { label: 'AI Purpose Tickets', icon: Microphone, path: '/review?type=PURPOSE_CREATE' },
      { label: 'Voice Bindings', icon: TreeStructure, path: '/review?type=BINDING_CREATE' },
      { label: 'Releases', icon: RocketLaunch, path: '/releases', disabled: true },
    ],
  },
  {
    label: 'Platform Config',
    items: [
      { label: 'Roles & Permissions', icon: Lock, path: '/permissions' },
      { label: 'App Categories', icon: FolderSimple, path: '/categories' },
      { label: 'AI Purposes', icon: Microphone, path: '/intents' },
      { label: 'Device Bindings', icon: DeviceMobile, path: '/device-bindings' },
      { label: 'Datasources', icon: Database, path: '/datasources', disabled: true },
    ],
  },
  {
    label: 'System Audit',
    items: [
      { label: 'Audit Logs', icon: ClipboardText, path: '/audit-logs' },
    ],
  },
];

function getAppStatusInfo(status?: string) {
  const norm = (status || 'DRAFT').toUpperCase();
  switch (norm) {
    case 'PUBLISHED':
    case 'APPROVED':
    case 'ACTIVE':
    case 'VALID':
      return { color: '#10B981', label: 'Active', bg: 'rgba(16, 185, 129, 0.15)' };
    case 'IN_REVIEW':
    case 'PENDING':
    case 'SUBMITTED':
      return { color: '#F59E0B', label: 'Review', bg: 'rgba(245, 158, 11, 0.15)' };
    case 'REJECTED':
    case 'SUSPENDED':
    case 'INVALID':
      return { color: '#EF4444', label: 'Rejected', bg: 'rgba(239, 68, 68, 0.15)' };
    case 'DRAFT':
    case 'INITIAL':
    case 'INACTIVE':
    default:
      return { color: '#9CA3AF', label: 'Draft', bg: 'rgba(156, 163, 175, 0.15)' };
  }
}

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();

  const isAdmin = user?.role === 'platform_admin' || user?.role === 'admin';
  const isDevSuperAdmin = useIsDeveloperSuperAdmin();

  // Developer apps query (only enabled for partner_dev)
  const { data: appsData, isLoading: isAppsLoading } = useDeveloperApps(
    undefined,
    { enabled: !isAdmin }
  );
  const developerApps = appsData?.items || [];

  // Collapsible apps menu state
  const [appsMenuOpen, setAppsMenuOpen] = useState(true);

  // Auto expand apps menu if navigating to an app detail page
  useEffect(() => {
    if (location.pathname.startsWith('/apps/')) {
      setAppsMenuOpen(true);
    }
  }, [location.pathname]);

  const isActive = (path: string) => {
    const currentFull = location.pathname + location.search;
    if (path === '/dashboard' || path === '/apps') {
      return location.pathname === path;
    }
    if (path.includes('?')) {
      return currentFull === path;
    }
    if (path === '/review') {
      return location.pathname === '/review' && !location.search;
    }
    return location.pathname.startsWith(path);
  };

  const isCurrentAppActive = (appId: string) => {
    return location.pathname.startsWith(`/apps/${appId}`);
  };

  const renderNavItem = (item: NavItem) => {
    const active = !item.disabled && isActive(item.path);
    const Icon = item.icon;

    const linkContent = (
      <div
        key={item.path + item.label}
        className={`${styles.navItem} ${active ? styles.navItemActive : ''} ${item.disabled ? styles.navItemDisabled : ''}`}
        onClick={() => {
          if (!item.disabled) {
            navigate(item.path);
          }
        }}
        style={{
          borderLeft: active
            ? `3px solid ${isAdmin ? 'var(--amber)' : 'var(--green)'}`
            : '3px solid transparent',
          background: active
            ? isAdmin
              ? 'var(--amber-dim)'
              : 'var(--green-dim)'
            : undefined,
        }}
      >
        <span
          className={styles.itemIcon}
          style={{
            color: active
              ? isAdmin
                ? 'var(--amber)'
                : 'var(--green)'
              : 'var(--text-secondary)',
          }}
        >
          <Icon size={18} weight={active ? 'fill' : 'duotone'} />
        </span>
        {!sidebarCollapsed && (
          <span
            className={styles.itemLabel}
            style={{
              color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
            }}
          >
            {item.label}
          </span>
        )}
      </div>
    );

    if (sidebarCollapsed || item.disabled) {
      return (
        <Tooltip
          key={item.path + item.label}
          label={item.disabled ? `${item.label} (Coming Soon)` : item.label}
          position="right"
          withArrow
          styles={{
            tooltip: {
              background: 'var(--surface)',
              border: '1px solid var(--boundary)',
              color: 'var(--text-primary)',
              fontSize: 12,
            },
          }}
        >
          {linkContent}
        </Tooltip>
      );
    }

    return linkContent;
  };

  return (
    <aside className={`${styles.sidebar} ${sidebarCollapsed ? styles.collapsed : ''}`}>
      {/* ── Logo area ─────────────────────────────────────────────────────── */}
      <div className={styles.logo}>
        {!sidebarCollapsed && (
          <Group gap={8}>
            <div
              className={styles.logoMark}
              style={{
                background: isAdmin ? 'var(--amber)' : 'var(--green)',
                color: '#FFFFFF',
              }}
            >
              {isAdmin ? 'A' : 'D'}
            </div>
            <div>
              <Text size="sm" fw={600} c="var(--text-primary)" lh={1.2}>
                HomeHub
              </Text>
              <Text size="xs" c="var(--text-secondary)" lh={1.2}>
                {isAdmin ? 'Admin Console' : 'Partner Portal'}
              </Text>
            </div>
          </Group>
        )}
        {sidebarCollapsed && (
          <div
            className={styles.logoMark}
            style={{
              background: isAdmin ? 'var(--amber)' : 'var(--green)',
              color: '#FFFFFF',
            }}
          >
            {isAdmin ? 'A' : 'D'}
          </div>
        )}
        <ActionIcon
          variant="subtle"
          size="sm"
          onClick={toggleSidebar}
          className={styles.collapseBtn}
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <ArrowRight size={14} weight="bold" />
          ) : (
            <ArrowLeft size={14} weight="bold" />
          )}
        </ActionIcon>
      </div>

      {/* ── Nav groups ────────────────────────────────────────────────────── */}
      <nav className={styles.nav}>
        {!isAdmin ? (
          /* DEVELOPER VIEW */
          <>
            {/* Group 1: Developer Workspace */}
            <div className={styles.navGroup}>
              {!sidebarCollapsed && <span className={styles.groupLabel}>Developer Workspace</span>}
              <Stack gap={2}>
                {renderNavItem({ label: 'Dashboard', icon: House, path: '/dashboard', disabled: true })}

                {/* Collapsible Applications Menu */}
                {!sidebarCollapsed ? (
                  <>
                    <div
                      className={styles.navItem}
                      onClick={() => setAppsMenuOpen(!appsMenuOpen)}
                      style={{
                        justifyContent: 'space-between',
                        paddingRight: 8,
                        background: location.pathname === '/apps' && !appsMenuOpen ? 'var(--surface-hi)' : undefined,
                      }}
                    >
                      <Group gap={10} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                        <span className={styles.itemIcon} style={{ color: 'var(--green)' }}>
                          <AppWindow size={18} weight="duotone" />
                        </span>
                        <span className={styles.itemLabel} style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                          Applications
                        </span>
                      </Group>

                      <Group gap={6} wrap="nowrap">
                        {developerApps.length > 0 && (
                          <Badge size="xs" variant="light" color="teal" style={{ padding: '0 6px', height: 18 }}>
                            {developerApps.length}
                          </Badge>
                        )}
                        {appsMenuOpen ? (
                          <CaretDown size={13} color="var(--text-secondary)" />
                        ) : (
                          <CaretRight size={13} color="var(--text-secondary)" />
                        )}
                      </Group>
                    </div>

                    <Collapse expanded={appsMenuOpen}>
                      <div className={styles.appSubList}>
                        {isAppsLoading ? (
                          <Stack gap={4} py={4}>
                            <Skeleton height={24} radius={4} style={{ background: 'var(--surface)' }} />
                            <Skeleton height={24} radius={4} style={{ background: 'var(--surface)' }} />
                          </Stack>
                        ) : developerApps.length === 0 ? (
                          <Text size="xs" c="var(--text-secondary)" fs="italic" px={8} py={4}>
                            Chưa có ứng dụng nào
                          </Text>
                        ) : (
                          developerApps.map((app: any) => {
                            const active = isCurrentAppActive(app.id);
                            const appName = app.name || 'Unnamed App';
                            const firstLetter = (appName[0] || 'A').toUpperCase();
                            const statusInfo = getAppStatusInfo(app.status);

                            return (
                              <div
                                key={app.id}
                                className={`${styles.appSubItem} ${active ? styles.appSubItemActive : ''}`}
                                onClick={() => navigate(`/apps/${app.id}`)}
                                title={`${appName} (${statusInfo.label})`}
                              >
                                <div className={styles.appMiniAvatar}>
                                  {app.iconUrl || app.imageUrl ? (
                                    <img src={app.iconUrl || app.imageUrl} alt={appName} />
                                  ) : (
                                    <span>{firstLetter}</span>
                                  )}
                                </div>

                                <Text
                                  size="xs"
                                  truncate
                                  style={{
                                    flex: 1,
                                    color: active ? '#10B981' : 'var(--text-primary)',
                                    fontWeight: active ? 600 : 400,
                                  }}
                                >
                                  {appName}
                                </Text>

                                {/* App Status Badge */}
                                <Tooltip
                                  label={`Trạng thái: ${app.status || statusInfo.label}`}
                                  position="right"
                                  withArrow
                                  styles={{ tooltip: { fontSize: 11 } }}
                                >
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      padding: '1px 5px',
                                      borderRadius: '3px',
                                      fontSize: '9px',
                                      fontWeight: 700,
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.04em',
                                      fontFamily: 'Geist Mono, monospace',
                                      color: statusInfo.color,
                                      background: statusInfo.bg,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {statusInfo.label}
                                  </span>
                                </Tooltip>
                              </div>
                            );
                          })
                        )}

                        {/* Quick Action: New App */}
                        <div
                          className={styles.createAppSubItem}
                          onClick={() => navigate('/apps/create')}
                        >
                          <Plus size={12} weight="bold" />
                          <span>Tạo ứng dụng mới</span>
                        </div>

                        {/* View All Apps Table Link */}
                        {developerApps.length > 0 && (
                          <div
                            className={`${styles.appSubItem} ${location.pathname === '/apps' ? styles.appSubItemActive : ''}`}
                            onClick={() => navigate('/apps')}
                            style={{ marginTop: 2, borderTop: '1px dashed var(--boundary)', paddingTop: 4 }}
                          >
                            <SquaresFour size={14} color="var(--text-secondary)" />
                            <Text size="xs" c="var(--text-secondary)">
                              Tất cả ứng dụng ({developerApps.length})
                            </Text>
                          </div>
                        )}
                      </div>
                    </Collapse>
                  </>
                ) : (
                  /* Collapsed Sidebar Mode: Popover Menu for Apps */
                  <Menu position="right-start" withArrow shadow="md" width={220}>
                    <Menu.Target>
                      <div
                        className={`${styles.navItem} ${location.pathname.startsWith('/apps') ? styles.navItemActive : ''}`}
                        style={{
                          borderLeft: location.pathname.startsWith('/apps') ? '3px solid var(--green)' : '3px solid transparent',
                          background: location.pathname.startsWith('/apps') ? 'var(--green-dim)' : undefined,
                        }}
                      >
                        <span className={styles.itemIcon} style={{ color: 'var(--green)' }}>
                          <AppWindow size={18} weight="duotone" />
                        </span>
                      </div>
                    </Menu.Target>
                    <Menu.Dropdown
                      styles={{
                        dropdown: {
                          background: 'var(--surface)',
                          border: '1px solid var(--boundary)',
                          padding: 6,
                        },
                      }}
                    >
                      <Menu.Label style={{ color: 'var(--text-secondary)', fontSize: 11 }}>
                        Ứng dụng của bạn ({developerApps.length})
                      </Menu.Label>
                      {developerApps.map((app: any) => {
                        const statusInfo = getAppStatusInfo(app.status);
                        return (
                          <Menu.Item
                            key={app.id}
                            onClick={() => navigate(`/apps/${app.id}`)}
                            rightSection={
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: '1px 4px',
                                  borderRadius: '3px',
                                  fontSize: '9px',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  fontFamily: 'Geist Mono, monospace',
                                  color: statusInfo.color,
                                  background: statusInfo.bg,
                                }}
                              >
                                {statusInfo.label}
                              </span>
                            }
                            styles={{
                              item: {
                                fontSize: 12,
                                color: isCurrentAppActive(app.id) ? '#10B981' : 'var(--text-primary)',
                                background: isCurrentAppActive(app.id) ? 'var(--green-dim)' : undefined,
                                fontWeight: isCurrentAppActive(app.id) ? 600 : 400,
                              },
                            }}
                          >
                            {app.name || 'Unnamed App'}
                          </Menu.Item>
                        );
                      })}
                      <Menu.Divider style={{ borderColor: 'var(--boundary)' }} />
                      <Menu.Item
                        leftSection={<Plus size={14} />}
                        onClick={() => navigate('/apps/create')}
                        styles={{ item: { fontSize: 12, color: '#10B981' } }}
                      >
                        Tạo ứng dụng mới
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<SquaresFour size={14} />}
                        onClick={() => navigate('/apps')}
                        styles={{ item: { fontSize: 12 } }}
                      >
                        Tất cả ứng dụng
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                )}
              </Stack>
            </div>

            {/* Group 2: Management & Governance (Super Admin only) */}
            {isDevSuperAdmin && (
              <div className={styles.navGroup}>
                {!sidebarCollapsed && <span className={styles.groupLabel}>Management & Governance</span>}
                <Stack gap={2}>
                  {renderNavItem({ label: 'Đội ngũ & Hoạt động', icon: Users, path: '/team' })}
                </Stack>
              </div>
            )}
          </>
        ) : (
          /* ADMIN VIEW */
          ADMIN_NAV_GROUPS.map((group) => (
            <div key={group.label} className={styles.navGroup}>
              {!sidebarCollapsed && <span className={styles.groupLabel}>{group.label}</span>}
              <Stack gap={2}>
                {group.items.map((item) => renderNavItem(item))}
              </Stack>
            </div>
          ))
        )}
      </nav>
    </aside>
  );
}

